import React from 'react';
import { Box, Container, Typography } from '@mui/material';

export default function PrivacyPolicyPage() {
  return (
    <Box
      sx={{ bgcolor: 'background.default', minHeight: '100vh', pt: 12, pb: 8 }}
    >
      <Container maxWidth="md">
        <Typography variant="h2" sx={{ fontWeight: 700, mb: 2 }}>
          Privacy Policy
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 6 }}>
          Last updated: December 31, 2025
        </Typography>

        <Box
          sx={{
            '& h3': { fontWeight: 600, mt: 4, mb: 2 },
            '& p': { mb: 2, lineHeight: 1.8 },
          }}
        >
          <Typography variant="h5" sx={{ fontWeight: 600, mb: 3 }}>
            1. Information We Collect
          </Typography>

          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, mt: 3 }}>
            Account Information
          </Typography>
          <Typography variant="body1">
            When you register for SimplyPayroll, we collect:
          </Typography>
          <Typography component="ul" variant="body1" sx={{ pl: 4 }}>
            <li>Business name and contact information</li>
            <li>Email address and password (encrypted)</li>
            <li>Payment information (processed securely by Stripe)</li>
            <li>Federal Employer Identification Number (FEIN) - encrypted</li>
          </Typography>

          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, mt: 3 }}>
            Contractor Information
          </Typography>
          <Typography variant="body1">
            To generate 1099 forms, we store:
          </Typography>
          <Typography component="ul" variant="body1" sx={{ pl: 4 }}>
            <li>Contractor names, addresses, and contact information</li>
            <li>
              Tax Identification Numbers (SSN/EIN) - encrypted with AES-256
            </li>
            <li>Payment records and work hours</li>
            <li>State tax IDs (when applicable) - encrypted</li>
          </Typography>

          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, mt: 3 }}>
            Usage Data
          </Typography>
          <Typography variant="body1">We automatically collect:</Typography>
          <Typography component="ul" variant="body1" sx={{ pl: 4 }}>
            <li>IP addresses and browser information</li>
            <li>Pages visited and features used</li>
            <li>Login timestamps and activity logs</li>
          </Typography>

          <Typography variant="h5" sx={{ fontWeight: 600, mb: 3, mt: 4 }}>
            2. How We Use Your Information
          </Typography>
          <Typography variant="body1">
            We use collected information to:
          </Typography>
          <Typography component="ul" variant="body1" sx={{ pl: 4 }}>
            <li>Provide and maintain the Service</li>
            <li>Generate accurate 1099 forms</li>
            <li>Process subscription payments</li>
            <li>Send service notifications and updates</li>
            <li>Improve our Service and user experience</li>
            <li>Detect and prevent fraud or abuse</li>
          </Typography>

          <Typography variant="h5" sx={{ fontWeight: 600, mb: 3, mt: 4 }}>
            3. Data Security
          </Typography>
          <Typography variant="body1">
            We take data security seriously and implement multiple layers of
            protection:
          </Typography>
          <Typography component="ul" variant="body1" sx={{ pl: 4 }}>
            <li>
              <strong>Encryption:</strong> All sensitive data (SSN, EIN, Tax
              IDs) is encrypted at rest using AES-256 encryption
            </li>
            <li>
              <strong>Secure Transmission:</strong> All data transmission uses
              TLS/SSL encryption (HTTPS)
            </li>
            <li>
              <strong>Access Controls:</strong> Role-based access limits who can
              view sensitive information
            </li>
            <li>
              <strong>Regular Audits:</strong> We conduct security audits and
              vulnerability assessments
            </li>
            <li>
              <strong>Secure Infrastructure:</strong> Hosted on secure,
              monitored servers with regular backups
            </li>
          </Typography>

          <Typography variant="h5" sx={{ fontWeight: 600, mb: 3, mt: 4 }}>
            4. Data Sharing and Disclosure
          </Typography>
          <Typography variant="body1">
            We do NOT sell your personal information. We may share data only in
            these circumstances:
          </Typography>
          <Typography component="ul" variant="body1" sx={{ pl: 4 }}>
            <li>
              <strong>Service Providers:</strong> Payment processors (Stripe)
              and hosting providers under strict confidentiality agreements
            </li>
            <li>
              <strong>Legal Requirements:</strong> When required by law,
              subpoena, or court order
            </li>
            <li>
              <strong>Business Transfers:</strong> In the event of a merger or
              acquisition, with prior notice
            </li>
          </Typography>

          <Typography variant="h5" sx={{ fontWeight: 600, mb: 3, mt: 4 }}>
            5. Data Retention
          </Typography>
          <Typography variant="body1">
            We retain your data for as long as your account is active. After
            account cancellation:
          </Typography>
          <Typography component="ul" variant="body1" sx={{ pl: 4 }}>
            <li>You have 30 days to download your data</li>
            <li>
              After 30 days, data is permanently deleted from active systems
            </li>
            <li>
              Backup copies are retained for 90 days for disaster recovery, then
              permanently deleted
            </li>
            <li>
              Tax-related records may be retained longer to comply with IRS
              record-keeping requirements (7 years)
            </li>
          </Typography>

          <Typography variant="h5" sx={{ fontWeight: 600, mb: 3, mt: 4 }}>
            6. Your Rights
          </Typography>
          <Typography variant="body1">You have the right to:</Typography>
          <Typography component="ul" variant="body1" sx={{ pl: 4 }}>
            <li>Access your personal data</li>
            <li>Request correction of inaccurate data</li>
            <li>
              Request deletion of your data (subject to legal retention
              requirements)
            </li>
            <li>Export your data in a portable format</li>
            <li>Opt-out of marketing communications</li>
            <li>Object to data processing</li>
          </Typography>
          <Typography variant="body1">
            To exercise these rights, contact us at privacy@simplypayroll.com
          </Typography>

          <Typography variant="h5" sx={{ fontWeight: 600, mb: 3, mt: 4 }}>
            7. Cookies and Tracking
          </Typography>
          <Typography variant="body1">
            We use cookies and similar technologies for:
          </Typography>
          <Typography component="ul" variant="body1" sx={{ pl: 4 }}>
            <li>
              <strong>Essential Cookies:</strong> Required for login and
              security (cannot be disabled)
            </li>
            <li>
              <strong>Analytics Cookies:</strong> Help us understand how users
              interact with the Service
            </li>
          </Typography>
          <Typography variant="body1">
            You can control cookies through your browser settings. Disabling
            certain cookies may limit functionality.
          </Typography>

          <Typography variant="h5" sx={{ fontWeight: 600, mb: 3, mt: 4 }}>
            8. Third-Party Services
          </Typography>
          <Typography variant="body1">
            We use the following third-party services:
          </Typography>
          <Typography component="ul" variant="body1" sx={{ pl: 4 }}>
            <li>
              <strong>Stripe:</strong> Payment processing (see Stripe's Privacy
              Policy)
            </li>
            <li>
              <strong>MongoDB Atlas:</strong> Database hosting (encrypted at
              rest)
            </li>
          </Typography>

          <Typography variant="h5" sx={{ fontWeight: 600, mb: 3, mt: 4 }}>
            9. Children's Privacy
          </Typography>
          <Typography variant="body1">
            SimplyPayroll is not intended for users under 18 years of age. We do
            not knowingly collect information from children.
          </Typography>

          <Typography variant="h5" sx={{ fontWeight: 600, mb: 3, mt: 4 }}>
            10. International Users
          </Typography>
          <Typography variant="body1">
            SimplyPayroll is based in the United States. If you access the
            Service from outside the US, your data will be transferred to and
            stored in the US. By using the Service, you consent to this
            transfer.
          </Typography>

          <Typography variant="h5" sx={{ fontWeight: 600, mb: 3, mt: 4 }}>
            11. Changes to This Policy
          </Typography>
          <Typography variant="body1">
            We may update this Privacy Policy from time to time. We will notify
            you of any material changes via email and by posting the new policy
            on this page. The "Last updated" date at the top will be revised.
          </Typography>

          <Typography variant="h5" sx={{ fontWeight: 600, mb: 3, mt: 4 }}>
            12. Contact Us
          </Typography>
          <Typography variant="body1">
            If you have questions about this Privacy Policy or your data:
          </Typography>
          <Typography variant="body1" sx={{ pl: 2 }}>
            Email: 4YourBusinessImage@gmail.com
          </Typography>
        </Box>
      </Container>
    </Box>
  );
}
